import {TabContext, TabPanel} from '@mui/lab'
import {Button, Typography, Divider} from '@mui/material'
import {Box} from '@mui/system'
import * as React from 'react'
import withLayout from '../../../hocs/Layout'
import PendingActionsIcon from '@mui/icons-material/PendingActions'
import PageTitle from '../../../common/PageTitle'
import {getMerchant} from '../../../helpers/ReduxHelpers'
import WeightDiscrepancyDetails from './discrepancy'
import WeightDiscrepancyAnalytics from './WDAnalytics'
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined'
import WeightDiscrepancyInstructions from './WeightDiscrepancyInstructions'
import WdHelp from './wdHelp'
import {PlayArrow} from '@mui/icons-material'
import {pageContainerSx, tabContainerSx, filterBarSx, primaryBtnSx} from '../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../styles/foundation/typography'
import {NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE, withOpacity} from '../../../styles/foundation/colors'

function WeightDiscrepancy() {
  const merchant = getMerchant()
  const merchantId = merchant.id ? merchant.id : ''
  const [value, setValue] = React.useState('1')
  const [helpModalOpen, setHelpModalOpen] = React.useState(false)

  // Same status-tab design as the Forward D2C Shipments page (list2/index.js):
  // a bordered white card of icon + label cells with an active colour underline.
  const tabs = [
    {label: 'Weight Discrepancy', icon: <PendingActionsIcon />, color: PRIMARY_BLUE, value: '1'},
    {label: 'Analytics', icon: <AnalyticsOutlinedIcon />, color: PRIMARY_BLUE, value: '2'},
  ]

  const openHelpModal = () => {
    setHelpModalOpen(true)
  }

  const closeHelpModal = () => {
    setHelpModalOpen(false)
  }

  return (
    <Box sx={pageContainerSx}>
      <PageTitle subTitle="Manage and track weight discrepancy claims with courier partners">Weight Discrepancy Management</PageTitle>

      {/* Header Section */}
      <Box sx={filterBarSx}>
        <Box
          sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2}}
        >
          <Button
            disableElevation
            sx={primaryBtnSx}
            variant='contained'
            size='small'
            onClick={openHelpModal}
            startIcon={<PlayArrow />}
          >
            Watch Videos
          </Button>
        </Box>

        <WeightDiscrepancyInstructions />
      </Box>

      {/* Tabs Section */}
      <TabContext value={value}>
        <Box sx={tabContainerSx}>
          {/* Status tabs — mirrors Forward D2C Shipments */}
          <Box sx={{bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', mb: 2, overflow: 'hidden'}}>
            <Box sx={{display: 'flex', overflowX: 'auto'}}>
              {tabs.map((t, i) => {
                const isActive = value === t.value
                return (
                  <React.Fragment key={t.value}>
                    {i > 0 && <Divider orientation='vertical' flexItem sx={{borderColor: BORDER}} />}
                    <Box
                      onClick={() => setValue(t.value)}
                      sx={{
                        flex: '1 1 auto', minWidth: 140, py: 1.25, px: 2, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                        borderBottom: isActive ? `3px solid ${t.color}` : '3px solid transparent',
                        bgcolor: isActive ? withOpacity(t.color, 0.06) : 'transparent',
                        transition: 'all 0.15s',
                        '&:hover': {bgcolor: withOpacity(t.color, 0.04)},
                      }}
                    >
                      <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: 30, height: 30, borderRadius: '8px', flexShrink: 0,
                        bgcolor: isActive ? withOpacity(t.color, 0.12) : withOpacity(t.color, 0.07),
                        color: isActive ? t.color : SUBTLE,
                        '& .MuiSvgIcon-root': {fontSize: 17},
                      }}>
                        {t.icon}
                      </Box>
                      <Typography sx={{
                        fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold,
                        color: isActive ? NAVY : SUBTLE, lineHeight: 1.2, whiteSpace: 'nowrap',
                      }}>
                        {t.label}
                      </Typography>
                    </Box>
                  </React.Fragment>
                )
              })}
            </Box>
          </Box>

          <TabPanel value='1' sx={{p: 0}}>
            <Box sx={{p: 3, minHeight: 400}}>
              <WeightDiscrepancyDetails />
            </Box>
          </TabPanel>

          <TabPanel value='2' sx={{p: 0}}>
            <Box sx={{p: 3, minHeight: 400}}>
              <WeightDiscrepancyAnalytics merchantId={merchantId} />
            </Box>
          </TabPanel>
        </Box>
      </TabContext>

      {/* Help Modal */}
      <WdHelp open={helpModalOpen} onClose={closeHelpModal} />
    </Box>
  )
}

export default withLayout(WeightDiscrepancy)
